/**
 * CDN Image Optimization Utility
 *
 * Generates responsive image attributes using CDN image transforms. The
 * transform syntax depends on the CDN host:
 *
 *   - Shopify (cdn.shopify.com) and Sapo (bizweb.dktcdn.net) accept
 *     the `_WIDTHx` suffix injected before the file extension
 *     (e.g. `img.jpg` → `img_600x.jpg`).
 *
 *   - Cloudflare R2 via the cdn-image-proxy Worker (cdn.eql-apparel.com)
 *     accepts a `?w=WIDTH` query string — see workers/cdn-image-proxy/
 *     src/index.ts. Injecting `_WIDTHx` into an R2 path 404s because no
 *     such object exists on the bucket.
 *
 *   - Any other host (or unknown extension) is returned untransformed.
 *
 * Why this matters: before this fix, the wholesale portal product cards
 * passed every URL through `getCardImageUrl()` which appended `_600x`,
 * so the ~220 products served from R2 backup images all 404'd their
 * thumbnails on /portal/products. The retail listing didn't hit this
 * because ProductCard.tsx uses the raw resolveProductMedia URL.
 *
 * Usage:
 *   <img {...getOptimizedImageProps(src, alt)} />
 *   <img {...getOptimizedImageProps(src, alt, { priority: true })} />
 */

const CDN_IMAGE_PATTERN = /\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i;

// Hosts that accept the `_WIDTHx` filename-suffix transform.
// Anything else gets passed through untouched (or via Cloudflare worker `?w=`).
//
// Why bizweb.dktcdn.net is NOT here even though Sapo *appears* to support
// the `_WIDTHx` convention: it only generates those thumbnails for a
// subset of products (pre-existing legacy SKUs). For products synced
// after the Sapo migration, the URLs use random-UUID filenames and the
// `_600x` variant 404s — see ace-navy-skirt /3d1bf620-…_600x.jpg test.
// Mirroring those to R2 (which the cdn-image-proxy Worker handles via
// `?w=`) is the durable fix; this list intentionally stays Shopify-only.
const SUFFIX_HOSTS = ["cdn.shopify.com"];

// Sapo/Bizweb DOES serve pre-generated thumbnails through a PATH prefix
// (`/thumb/<size>/100/...`) even for the UUID-named post-migration files
// that lack `_600x` variants — verified 2026-09-07 on random catalog images
// (all 200). Sizes are height-based: large = 384×480, grande = 480×600,
// 1024x1024 = 820×1024. With the browser's `image/webp` Accept header the
// CDN returns WebP for these, ~2.5–4× smaller than the original JPEG.
const SAPO_HOSTS = ["bizweb.dktcdn.net"];
const SAPO_THUMBS: Array<{ maxWidth: number; name: string }> = [
  { maxWidth: 384, name: "large" },
  { maxWidth: 480, name: "grande" },
  { maxWidth: 820, name: "1024x1024" },
];

// Hosts whose images are served by our Cloudflare worker, which accepts ?w=NNN.
const WORKER_HOSTS = ["cdn.eql-apparel.com"];

interface ImageSize {
  width: number;
  suffix: string;
}

const RESPONSIVE_SIZES: ImageSize[] = [
  { width: 200, suffix: "_200x" },
  { width: 400, suffix: "_400x" },
  { width: 600, suffix: "_600x" },
  { width: 800, suffix: "_800x" },
  { width: 1200, suffix: "_1200x" },
];

/**
 * Transform a CDN image URL to a specific width using the right syntax
 * for the URL's host (filename-suffix for Shopify/Sapo, ?w= for the R2
 * worker, pass-through for anything else).
 */
export function getCdnImageUrl(src: string, width: number): string {
  if (!src || !CDN_IMAGE_PATTERN.test(src)) return src;

  // Try to parse the URL host. Bail out (return src) on malformed URLs
  // so a junk value doesn't produce a 404 transform.
  let host = "";
  try {
    host = new URL(src).hostname;
  } catch {
    return src;
  }

  // R2 via worker — append ?w=NNN (or replace existing one).
  if (WORKER_HOSTS.includes(host)) {
    try {
      const u = new URL(src);
      u.searchParams.set("w", String(width));
      return u.toString();
    } catch {
      return src;
    }
  }

  // Shopify / Sapo — inject `_WIDTHx` before the extension.
  if (SUFFIX_HOSTS.includes(host)) {
    return src.replace(
      /(\.(jpg|jpeg|png|webp|gif))(\?.*)?$/i,
      `_${width}x$1$3`,
    );
  }

  if (SAPO_HOSTS.includes(host)) {
    const thumb = SAPO_THUMBS.find((t) => width <= t.maxWidth);
    if (!thumb) return src; // wider than any thumbnail → original
    try {
      const u = new URL(src);
      if (u.pathname.startsWith("/thumb/")) return src; // already a thumb
      u.pathname = `/thumb/${thumb.name}${u.pathname}`;
      return u.toString();
    } catch {
      return src;
    }
  }

  // Unknown host — return untransformed; the browser still loads it,
  // just at native resolution. Better than a 404.
  return src;
}

/**
 * Candidate widths for a `srcset`. Sapo only has three thumbnail rungs, so
 * for Sapo hosts we emit exactly those instead of the generic 200–1200
 * ladder — the browser must be told the REAL intrinsic width of each
 * candidate or it picks wrong.
 */
export function getResponsiveCandidates(src: string): Array<{ url: string; width: number }> {
  if (!src || !CDN_IMAGE_PATTERN.test(src)) return [];
  let host = "";
  try {
    host = new URL(src).hostname;
  } catch {
    return [];
  }
  if (SAPO_HOSTS.includes(host)) {
    if (src.includes("/thumb/")) return [];
    return SAPO_THUMBS.map((t) => ({ url: getCdnImageUrl(src, t.maxWidth), width: t.maxWidth }));
  }
  if (WORKER_HOSTS.includes(host) || SUFFIX_HOSTS.includes(host)) {
    return RESPONSIVE_SIZES.map(({ width }) => ({ url: getCdnImageUrl(src, width), width }));
  }
  return [];
}

/** `srcset` string for any product image host we know how to resize; "" otherwise. */
export function getProductSrcSet(src: string): string {
  return getResponsiveCandidates(src)
    .map((c) => `${c.url} ${c.width}w`)
    .join(", ");
}

/** The storefront grid: 2 columns to lg, 3 to xl, then 4. */
export const GRID_CARD_SIZES = "(max-width: 1023px) 50vw, (max-width: 1279px) 33vw, 25vw";

/**
 * Generate srcSet string for responsive images
 */
export function getSrcSet(src: string, sizes?: ImageSize[]): string {
  if (!src || !CDN_IMAGE_PATTERN.test(src)) return "";

  const useSizes = sizes || RESPONSIVE_SIZES;
  return useSizes
    .map(({ width }) => `${getCdnImageUrl(src, width)} ${width}w`)
    .join(", ");
}

interface OptimizedImageProps {
  src: string;
  alt: string;
  srcSet?: string;
  sizes?: string;
  loading?: "lazy" | "eager";
  fetchpriority?: "high" | "low" | "auto";
  decoding?: "async" | "sync" | "auto";
}

interface ImageOptions {
  /** Whether this is an above-the-fold image (sets eager loading + high priority) */
  priority?: boolean;
  /** Custom sizes attribute. Defaults to responsive breakpoints. */
  sizes?: string;
  /** Custom widths for srcSet generation */
  widths?: number[];
}

/**
 * Get optimized image props for a CDN-hosted image.
 *
 * @example
 * // Basic usage with lazy loading
 * <img {...getOptimizedImageProps(product.image, product.title)} />
 *
 * // Priority image (above the fold)
 * <img {...getOptimizedImageProps(product.image, product.title, { priority: true })} />
 *
 * // Custom sizes
 * <img {...getOptimizedImageProps(src, alt, { sizes: "(max-width: 640px) 100vw, 50vw" })} />
 */
export function getOptimizedImageProps(
  src: string,
  alt: string,
  options?: ImageOptions
): OptimizedImageProps {
  const { priority = false, sizes, widths } = options || {};

  const customSizes = widths
    ? widths.map((w) => ({ width: w, suffix: `_${w}x` }))
    : undefined;

  const srcSet = getSrcSet(src, customSizes);

  const props: OptimizedImageProps = {
    src,
    alt,
    decoding: "async",
  };

  if (srcSet) {
    props.srcSet = srcSet;
    props.sizes = sizes || "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";
  }

  if (priority) {
    props.loading = "eager";
    props.fetchpriority = "high";
  } else {
    props.loading = "lazy";
  }

  return props;
}

/**
 * Get a thumbnail URL (200px wide) for lists and grids
 */
export function getThumbnailUrl(src: string): string {
  return getCdnImageUrl(src, 200);
}

/**
 * Get a medium-sized URL (600px wide) for product cards
 */
export function getCardImageUrl(src: string): string {
  return getCdnImageUrl(src, 600);
}

/**
 * Get a large URL (1200px wide) for product detail pages
 */
export function getDetailImageUrl(src: string): string {
  return getCdnImageUrl(src, 1200);
}
